import { useState, useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import { fetchHangar, updateHangar } from '@/lib/api/hangars'
import { spaceManagementKeys } from '@/hooks/useSpaceManagement'
import {
  HANGAR_STATUS_LABELS,
  HANGAR_STATUSES,
  PRICING_MODEL_LABELS,
  PRICING_MODELS,
} from '@/types/hangars'

// ============================================
// FORM SCHEMA
// ============================================

const areaEditSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  area_code: z.string().min(1, 'Area code is required').max(20),
  status: z.enum(['active', 'maintenance', 'construction', 'inactive']),
  // Pricing
  monthly_rate_private: z.number().nullable(),
  monthly_rate_shared: z.number().nullable(),
  transient_rate_nightly: z.number().nullable(),
  rate_per_sqft_private: z.number().nullable(),
  rate_per_sqft_shared: z.number().nullable(),
  pricing_model: z.enum(['fixed', 'per_sqft']),
  // Features
  construction_year: z.number().nullable(),
  fire_suppression: z.boolean(),
  security_system: z.boolean(),
})

type AreaEditFormValues = z.infer<typeof areaEditSchema>

// ============================================
// PROPS
// ============================================

interface AreaEditDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  areaId: string | undefined
}

// ============================================
// COMPONENT
// ============================================

export function AreaEditDrawer({
  open,
  onOpenChange,
  areaId,
}: AreaEditDrawerProps) {
  const queryClient = useQueryClient()

  // Exit confirmation
  const [showExitConfirm, setShowExitConfirm] = useState(false)
  const [pendingClose, setPendingClose] = useState(false)

  // Fetch area data
  const { data: area, isLoading } = useQuery({
    queryKey: ['area', areaId],
    queryFn: () => fetchHangar(areaId!),
    enabled: !!areaId && open,
  })

  // Update mutation
  const mutation = useMutation({
    mutationFn: (values: AreaEditFormValues) =>
      updateHangar(areaId!, {
        name: values.name,
        area_code: values.area_code,
        status: values.status,
        monthly_rate_private: values.monthly_rate_private,
        monthly_rate_shared: values.monthly_rate_shared,
        transient_rate_nightly: values.transient_rate_nightly,
        rate_per_sqft_private: values.rate_per_sqft_private,
        rate_per_sqft_shared: values.rate_per_sqft_shared,
        pricing_model: values.pricing_model,
        construction_year: values.construction_year,
        fire_suppression: values.fire_suppression,
        security_system: values.security_system,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceManagementKeys.all })
      queryClient.invalidateQueries({ queryKey: ['area', areaId] })
      form.reset()
      onOpenChange(false)
    },
  })

  // Form
  const form = useForm<AreaEditFormValues>({
    resolver: zodResolver(areaEditSchema),
    defaultValues: {
      name: '',
      area_code: '',
      status: 'active',
      monthly_rate_private: null,
      monthly_rate_shared: null,
      transient_rate_nightly: null,
      rate_per_sqft_private: null,
      rate_per_sqft_shared: null,
      pricing_model: 'fixed',
      construction_year: null,
      fire_suppression: false,
      security_system: false,
    },
  })

  const { isDirty } = form.formState

  // Populate form when area loads
  useEffect(() => {
    if (area) {
      form.reset({
        name: area.name,
        area_code: area.area_code,
        status: area.status,
        monthly_rate_private: area.monthly_rate_private,
        monthly_rate_shared: area.monthly_rate_shared,
        transient_rate_nightly: area.transient_rate_nightly,
        rate_per_sqft_private: area.rate_per_sqft_private,
        rate_per_sqft_shared: area.rate_per_sqft_shared,
        pricing_model: area.pricing_model,
        construction_year: area.construction_year,
        fire_suppression: area.fire_suppression ?? false,
        security_system: area.security_system ?? false,
      })
    }
  }, [area, form])

  // Handle drawer close with dirty check
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && isDirty) {
        setPendingClose(true)
        setShowExitConfirm(true)
        return
      }
      onOpenChange(newOpen)
    },
    [isDirty, onOpenChange]
  )

  const handleConfirmClose = useCallback(() => {
    setShowExitConfirm(false)
    if (pendingClose) {
      setPendingClose(false)
      form.reset()
      onOpenChange(false)
    }
  }, [pendingClose, onOpenChange, form])

  const handleCancelClose = useCallback(() => {
    setShowExitConfirm(false)
    setPendingClose(false)
  }, [])

  const onSubmit = (values: AreaEditFormValues) => {
    mutation.mutate(values)
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent
          side="right"
          className="flex flex-col w-full sm:max-w-lg p-0 overflow-hidden"
        >
          <SheetHeader className="px-4 py-4 border-b shrink-0">
            <SheetTitle>
              Edit Building: {area?.name || '...'}
            </SheetTitle>
            <SheetDescription>
              Update building properties, pricing, and features.
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 min-h-0">
            <Form {...form}>
              <form
                id="area-edit-form"
                onSubmit={form.handleSubmit(onSubmit)}
                className="px-4 py-4 space-y-6"
              >
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Building name"
                          disabled={mutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Area Code */}
                <FormField
                  control={form.control}
                  name="area_code"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Area Code *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="HGR-01"
                          className="font-mono uppercase"
                          onChange={(e) =>
                            field.onChange(e.target.value.toUpperCase())
                          }
                          disabled={mutation.isPending}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Status */}
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={mutation.isPending}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {HANGAR_STATUSES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {HANGAR_STATUS_LABELS[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Separator />

                {/* Pricing Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Pricing</h3>

                  <FormField
                    control={form.control}
                    name="pricing_model"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Pricing Model</FormLabel>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                          disabled={mutation.isPending}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {PRICING_MODELS.map((m) => (
                              <SelectItem key={m} value={m}>
                                {PRICING_MODEL_LABELS[m]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="monthly_rate_private"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Monthly (Private) $</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null
                                )
                              }
                              disabled={mutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="monthly_rate_shared"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Monthly (Shared) $</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null
                                )
                              }
                              disabled={mutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="transient_rate_nightly"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Transient Rate (Nightly) $</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  ? parseFloat(e.target.value)
                                  : null
                              )
                            }
                            disabled={mutation.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="rate_per_sqft_private"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>$/sqft (Private)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null
                                )
                              }
                              disabled={mutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="rate_per_sqft_shared"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>$/sqft (Shared)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null
                                )
                              }
                              disabled={mutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                <Separator />

                {/* Features Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium">Features</h3>

                  <FormField
                    control={form.control}
                    name="construction_year"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Construction Year</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            value={field.value ?? ''}
                            onChange={(e) =>
                              field.onChange(
                                e.target.value
                                  ? parseInt(e.target.value)
                                  : null
                              )
                            }
                            placeholder="e.g. 2015"
                            disabled={mutation.isPending}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="fire_suppression"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel className="!mt-0">
                          Fire Suppression
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={mutation.isPending}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="security_system"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between">
                        <FormLabel className="!mt-0">
                          Security System
                        </FormLabel>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            disabled={mutation.isPending}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            </Form>
          </ScrollArea>

          <SheetFooter className="flex-row justify-end gap-2 px-4 py-4 border-t bg-background shrink-0">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="area-edit-form"
              disabled={mutation.isPending || isLoading}
            >
              {mutation.isPending ? 'Saving...' : 'Update'}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Exit Confirmation Dialog */}
      <AlertDialog open={showExitConfirm} onOpenChange={setShowExitConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close? Your
              changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmClose}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
